import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Collapse,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    Grid,
    Card,
    CardContent,
    TablePagination
} from '@mui/material';
import {
    KeyboardArrowDown,
    KeyboardArrowUp,
    Business,
    Message,
    People,
    TrendingUp,
} from '@mui/icons-material';
import { companyAPI } from '../../services/api';
import './CompanyReport.css';

// Row component with collapsible user details
function CompanyRow({ company }) {
    const [open, setOpen] = useState(false);

    return (
        <>
            <TableRow
                sx={{
                    '& > *': { borderBottom: 'unset' },
                    backgroundColor: open ? 'action.hover' : 'inherit',
                    '&:hover': { backgroundColor: 'action.hover' }
                }}
            >
                <TableCell>
                    <IconButton
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business fontSize="small" color="primary" />
                        <Typography variant="body2" fontWeight={600}>
                            {company.company_name}
                        </Typography>
                    </Box>
                </TableCell>
                <TableCell>
                    <Chip
                        label={company.is_active ? 'Active' : 'Inactive'}
                        color={company.is_active ? 'success' : 'default'}
                        size="small"
                    />
                </TableCell>
                <TableCell align="center">
                    <Chip
                        label={`${company.active_users}/${company.total_users}`}
                        color="primary"
                        size="small"
                        variant="outlined"
                    />
                </TableCell>
                <TableCell align="center">
                    <Typography variant="body2" fontWeight={600}>
                        {company.total_messages}
                    </Typography>
                </TableCell>
                <TableCell align="center">
                    <Typography variant="body2" fontWeight={600} color="primary">
                        {company.unique_customers}
                    </Typography>
                </TableCell>
                <TableCell align="center">
                    <Chip
                        label={company.customers_today}
                        color="success"
                        size="small"
                    />
                </TableCell>
                <TableCell>
                    <Typography variant="caption" color="text.secondary">
                        {company.last_message_at
                            ? new Date(company.last_message_at).toLocaleString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })
                            : 'No messages'
                        }
                    </Typography>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                            <Typography variant="h6" gutterBottom component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <People fontSize="small" />
                                User Breakdown
                            </Typography>

                            {company.users && company.users.length > 0 ? (
                                <>
                                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600, color: 'primary.main' }}>
                                        Web Chat Statistics
                                    </Typography>
                                    <Table size="small" sx={{ mb: 3 }}>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#1976d2' }}>
                                                <TableCell sx={{ fontWeight: 600, color: '#000 !important' }}>User Name</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: '#000 !important' }}>Email</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: '#000 !important' }} align="center">Status</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: '#000 !important' }} align="center">Messages</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: '#000 !important' }} align="center">Customers</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: '#000 !important' }}>Last Activity</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {company.users.map((user) => (
                                                <TableRow
                                                    key={`web-${user.user_id}`}
                                                    sx={{
                                                        '&:last-child td, &:last-child th': { border: 0 },
                                                        opacity: user.is_active ? 1 : 0.6
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {user.full_name || 'N/A'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {user.email}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={user.is_active ? 'Active' : 'Inactive'}
                                                            color={user.is_active ? 'success' : 'default'}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2">
                                                            {user.total_web_messages || 0}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2" color="primary">
                                                            {user.unique_web_customers || 0}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {user.last_web_activity
                                                                ? new Date(user.last_web_activity).toLocaleString('en-GB', {
                                                                    day: '2-digit',
                                                                    month: 'short',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })
                                                                : 'No activity'
                                                            }
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>

                                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600, color: 'success.main' }}>
                                        WhatsApp Statistics
                                    </Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#2e7d32' }}>
                                                <TableCell sx={{ fontWeight: 600, color: '#000 !important' }}>User Name</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: '#000 !important' }}>Email</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: '#000 !important' }} align="center">Status</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: '#000 !important' }} align="center">Messages</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: '#000 !important' }} align="center">Customers</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: '#000 !important' }}>Last Activity</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {company.users.map((user) => (
                                                <TableRow
                                                    key={`whatsapp-${user.user_id}`}
                                                    sx={{
                                                        '&:last-child td, &:last-child th': { border: 0 },
                                                        opacity: user.is_active ? 1 : 0.6
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {user.full_name || 'N/A'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {user.email}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={user.is_active ? 'Active' : 'Inactive'}
                                                            color={user.is_active ? 'success' : 'default'}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2">
                                                            {user.total_whatsapp_messages || 0}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2" color="success.main">
                                                            {user.unique_whatsapp_customers || 0}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {user.last_whatsapp_activity
                                                                ? new Date(user.last_whatsapp_activity).toLocaleString('en-GB', {
                                                                    day: '2-digit',
                                                                    month: 'short',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })
                                                                : 'No activity'
                                                            }
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </>
                            ) : (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    No users found for this company
                                </Alert>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

const CompanyReport = () => {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pagination, setPagination] = useState(null);

    useEffect(() => {
        fetchReport();
    }, [page, rowsPerPage]);

    const fetchReport = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await companyAPI.getCompanyWiseReport(page + 1, rowsPerPage);
            setReportData(response.data.data);
            setPagination(response.data.pagination);
        } catch (err) {
            console.error('Error fetching company report:', err);
            setError('Failed to load company report. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {error}
            </Alert>
        );
    }

    const { companies, summary } = reportData || {};

    return (
        <Box className="company-report">
            <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                Company-Wise Analytics Report
            </Typography>

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                                        {summary?.total_companies || 0}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                        Total Companies
                                    </Typography>
                                </Box>
                                <Business sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                                        {summary?.active_companies || 0}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                        Active Companies
                                    </Typography>
                                </Box>
                                <TrendingUp sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                                        {summary?.total_web_messages?.toLocaleString() || 0}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                        Web Messages
                                    </Typography>
                                </Box>
                                <Message sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                                        {summary?.total_whatsapp_messages?.toLocaleString() || 0}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                        WhatsApp Messages
                                    </Typography>
                                </Box>
                                <Message sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                                        {summary?.total_customers?.toLocaleString() || 0}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                        Total Customers
                                    </Typography>
                                </Box>
                                <People sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Company Table */}
            <TableContainer component={Paper} elevation={3}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#1976d2' }}>
                            <TableCell sx={{ width: 50, color: '#ffffff !important' }} />
                            <TableCell sx={{ color: '#000 !important', fontWeight: 600 }}>Company Name</TableCell>
                            <TableCell sx={{ color: '#000 !important', fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ color: '#000 !important', fontWeight: 600 }} align="center">Users</TableCell>
                            <TableCell sx={{ color: '#000 !important', fontWeight: 600 }} align="center">Total Messages</TableCell>
                            <TableCell sx={{ color: '#000 !important', fontWeight: 600 }} align="center">Customers</TableCell>
                            <TableCell sx={{ color: '#000 !important', fontWeight: 600 }} align="center">Active Today</TableCell>
                            <TableCell sx={{ color: '#000 !important', fontWeight: 600 }}>Last Message</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {companies && companies.length > 0 ? (
                            companies.map((company) => (
                                <CompanyRow key={company.id} company={company} />
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <Alert severity="info">No companies found</Alert>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {pagination && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <TablePagination
                        component="div"
                        count={pagination.totalRecords || 0}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        labelRowsPerPage="Companies per page:"
                    />
                </Box>
            )}
        </Box>
    );
};

export default CompanyReport;
